
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Hook for authentication methods
 */
export const useAuthMethods = (
  setIsLoading: (isLoading: boolean) => void
) => {
  const signUp = async (email: string, password: string, userData: any = {}) => {
    try {
      setIsLoading(true);
      
      const { error, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/auth`,
        }
      });
      
      if (error) {
        console.error('Sign up error:', error);
        toast.error(error.message);
        throw error;
      }
      
      console.log('Sign up successful:', data);
      return data;
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('Sign in error:', error);
        toast.error(error.message);
        throw error;
      }
      
      console.log('Sign in successful:', data);
      return data;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign out error:', error);
        toast.error(error.message);
        throw error;
      }
      
      console.log('Sign out successful');
    } finally {
      setIsLoading(false);
    }
  };

  const updateEmail = async (newEmail: string) => {
    try {
      setIsLoading(true);
      
      // Fix: Removing the nested options object as it's not supported in the UserAttributes type
      // Instead, use the emailRedirectTo in the second options parameter of updateUser
      const { error, data } = await supabase.auth.updateUser(
        { email: newEmail },
        {
          emailRedirectTo: `${window.location.origin}/profile?tab=profile`,
        }
      );
      
      if (error) {
        console.error('Update email error:', error);
        toast.error(error.message);
        throw error;
      }
      
      toast.success("Email update initiated! Please check your inbox for verification.");
      console.log('Update email initiated:', data);
      return data;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    try {
      setIsLoading(true);

      const {
        data: { user },
        error: getUserError,
      } = await supabase.auth.getUser();

      if (getUserError || !user?.email) {
        console.error('Could not retrieve user email:', getUserError);
        toast.error('Unable to verify identity. Please try signing in again.');
        throw new Error('Unable to retrieve current user email');
      }

      const email = user.email;

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });

      if (signInError) {
        console.error('Password verification failed:', signInError);
        toast.error("Current password is incorrect");
        throw new Error("Current password is incorrect");
      }

      const { error, data } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        console.error('Update password error:', error);
        toast.error(error.message);
        throw error;
      }

      toast.success("Password updated successfully!");
      console.log('Password update successful:', data);
      return data;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signUp,
    signIn,
    signOut,
    updateEmail,
    updatePassword
  };
};
